import { useRef, useState, type FormEvent } from "react";
import { Link } from "wouter";
import { Loader2 } from "lucide-react";
import { PublicHeader } from "@/components/public-header";
import { PublicFooter } from "@/components/public-footer";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useSEO } from "@/hooks/use-seo";
import { useSyreOffer } from "@/hooks/use-syre-offer";
import { SIGNUP_SEO } from "@/data/marketing-seo";
import { track } from "@/lib/marketing/analytics";
import { redirectTo } from "@/lib/marketing/navigation";
import { attributionForSignup } from "@/lib/marketing/attribution";
import { LOGIN_PATH, TERMS_PRICING_HREF } from "@/lib/marketing/links";
import { TRIAL_DAYS, formatUsd } from "@/lib/marketing/pricing-copy";
import { parseUnits, parseEventId, lifetimeClaimPath } from "@/lib/marketing/syre-offer";
import { stashReturnTo } from "@/lib/auth-return-to";
import { resetSessionCache } from "@/lib/queryClient";
import {
  submitSignup,
  toE164,
  verifySignupCode,
  type SignupPayload,
  type SignupRole,
} from "@/lib/marketing/signup-api";

const ROLE_OPTIONS: Array<{ value: SignupRole; label: string }> = [
  { value: "host", label: "PadSplit host" },
  { value: "property_manager", label: "Property manager" },
  { value: "researcher", label: "Researching my first property" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FieldErrors = Partial<Record<"name" | "email" | "propertyCount" | "role" | "phone" | "smsConsent" | "acceptedTerms" | "code" | "form", string>>;
const KNOWN_FIELDS = new Set(["name", "email", "propertyCount", "role", "phone", "smsConsent", "acceptedTerms", "code"]);

function roleFromSearch(search: string): SignupRole | "" {
  const role = new URLSearchParams(search).get("role");
  return role === "host" || role === "property_manager" || role === "researcher" ? role : "";
}

function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <p id={id} className="mt-1.5 text-sm text-red-600" role="alert" data-testid={`error-${id}`}>
      {message}
    </p>
  );
}

/**
 * Public signup (build F11, Appendix A4; passwordless per Travis D3).
 * Step 1 posts the form; step 2 verifies the emailed 6-digit code and
 * sends the visitor to the `redirectTo` the backend returns.
 */
export default function SignupPage() {
  useSEO({ title: SIGNUP_SEO.title, description: SIGNUP_SEO.description, path: SIGNUP_SEO.path });

  const search = typeof window !== "undefined" ? window.location.search : "";
  const wantsLifetime = new URLSearchParams(search).get("offer") === "syre-lifetime";
  const { offer } = useSyreOffer();
  const lifetimeOpen = wantsLifetime && offer.open;
  const lifetimeUnits = parseUnits(new URLSearchParams(search).get("units"), offer.maxUnits);
  const lifetimeEventId = parseEventId(new URLSearchParams(search).get("eventId")) ?? offer.eventId;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [propertyCount, setPropertyCount] = useState("");
  const [role, setRole] = useState<SignupRole | "">(() => roleFromSearch(search));
  const [showPhone, setShowPhone] = useState(false);
  const [phone, setPhone] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [pending, setPending] = useState(false);
  const [step, setStep] = useState<"form" | "code">("form");
  const [code, setCode] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [resent, setResent] = useState(false);
  const lastPayload = useRef<SignupPayload | null>(null);
  const startedRef = useRef(false);

  const markStarted = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    track("signup_started");
  };

  const submitLabel = wantsLifetime
    ? "Create account and continue to checkout"
    : role === "researcher"
      ? "Create free account"
      : "Start free trial";

  function validate(): FieldErrors {
    const next: FieldErrors = {};
    if (name.trim().length < 2) next.name = "Enter your full name.";
    if (!EMAIL_RE.test(email.trim())) next.email = "Enter a valid email address.";
    const count = Number(propertyCount);
    if (propertyCount.trim() === "" || !Number.isInteger(count) || count < 0) {
      next.propertyCount = "Enter how many properties you run. 0 is fine.";
    }
    if (!role) next.role = "Choose the option that fits you best.";
    if (phone.trim() && !toE164(phone)) next.phone = "Enter a valid phone number, or leave it blank.";
    // The contract requires SMS consent whenever a phone number is sent.
    else if (phone.trim() && !smsConsent) next.smsConsent = "Check the box to get text alerts, or leave the phone number blank.";
    if (!acceptedTerms) next.acceptedTerms = "Please agree to the Terms of Service and Privacy Policy.";
    return next;
  }

  function applyServerError(field: string | undefined, message: string) {
    if (field && KNOWN_FIELDS.has(field)) setErrors({ [field]: message } as FieldErrors);
    else setErrors({ form: message });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (pending) return;
    markStarted();
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const e164 = toE164(phone);
    const payload: SignupPayload = {
      name: name.trim(),
      email: email.trim().toLowerCase(),
      propertyCount: Number(propertyCount),
      role: role as SignupRole,
      ...(e164 ? { phone: e164, smsConsent: true } : {}),
      acceptedTerms: true,
      ...(wantsLifetime ? { offer: "syre-lifetime" as const, units: lifetimeUnits, eventId: lifetimeEventId } : {}),
      attribution: attributionForSignup(),
    };
    track("signup_submitted", { role: payload.role });
    setPending(true);
    const result = await submitSignup(payload);
    setPending(false);
    if (!result.ok) {
      if (wantsLifetime) stashReturnTo(lifetimeClaimPath(lifetimeUnits, lifetimeEventId));
      applyServerError(result.field, result.message);
      return;
    }
    lastPayload.current = payload;
    setSubmittedEmail(payload.email);
    setStep("code");
  }

  async function handleVerify(e: FormEvent) {
    e.preventDefault();
    if (pending) return;
    const trimmed = code.replace(/\D/g, "");
    if (trimmed.length !== 6) {
      setErrors({ code: "Enter the 6-digit code from your email." });
      return;
    }
    setErrors({});
    setPending(true);
    const result = await verifySignupCode(lastPayload.current!, trimmed);
    if (!result.ok) {
      setPending(false);
      applyServerError(result.field ?? "code", result.message);
      return;
    }
    resetSessionCache();
    redirectTo(result.redirectTo);
  }

  async function handleResend() {
    if (pending || !lastPayload.current) return;
    setPending(true);
    const result = await submitSignup(lastPayload.current);
    setPending(false);
    if (result.ok) {
      setResent(true);
      setErrors({});
    } else {
      applyServerError(undefined, result.message);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <PublicHeader />
      <main className="flex-grow px-4 pb-16 pt-28 md:pt-32">
        <div className="mx-auto w-full max-w-lg">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900" data-testid="text-signup-heading">
            {wantsLifetime ? "Create your lifetime purchase account" : `Start your ${TRIAL_DAYS}-day free trial`}
          </h1>
          <p className="mt-2 text-gray-600" data-testid="text-signup-sub">
            {wantsLifetime ? "Verify your email, then pay once through Stripe before setup. No separate trial or card-setup checkout." : "No card needed. Connect PadSplit in about 3 minutes."}
          </p>

          {lifetimeOpen && (
            <div
              className="mt-6 rounded-xl border border-brand-accent/40 bg-brand-accent/10 p-4 text-sm text-gray-900"
              data-testid="banner-syre-lifetime"
            >
              SYRE lifetime deal: create your account, then check out. {lifetimeUnits * offer.propertyAllowancePerUnit} properties for
              life, {formatUsd(lifetimeUnits * offer.priceCents)} once.
            </div>
          )}
          {wantsLifetime && !offer.open && <p className="mt-4" role="status">The lifetime offer is currently unavailable. Your selection is retained; no purchase will be replaced with a trial automatically. <a href={`/syre?units=${lifetimeUnits}`} className="underline">Return to the offer</a>.</p>}

          <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 sm:p-8 shadow-sm">
            {step === "form" ? (
              <form
                onSubmit={handleSubmit}
                onFocusCapture={markStarted}
                noValidate
                className="space-y-5"
                data-testid="form-signup"
              >
                <div>
                  <Label htmlFor="signup-name">Full name</Label>
                  <Input
                    id="signup-name"
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    aria-invalid={Boolean(errors.name)}
                    aria-describedby={errors.name ? "signup-name-error" : undefined}
                    className="mt-1.5"
                    data-testid="input-signup-name"
                  />
                  <FieldError id="signup-name-error" message={errors.name} />
                </div>

                <div>
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? "signup-email-error" : undefined}
                    className="mt-1.5"
                    data-testid="input-signup-email"
                  />
                  <FieldError id="signup-email-error" message={errors.email} />
                </div>

                <div>
                  <Label htmlFor="signup-properties">How many properties do you run?</Label>
                  <Input
                    id="signup-properties"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    value={propertyCount}
                    onChange={(e) => setPropertyCount(e.target.value)}
                    aria-invalid={Boolean(errors.propertyCount)}
                    aria-describedby="signup-properties-help"
                    className="mt-1.5"
                    data-testid="input-signup-properties"
                  />
                  <p id="signup-properties-help" className="mt-1.5 text-xs text-gray-500">
                    On or off PadSplit. Enter 0 if you're still buying your first.
                  </p>
                  <FieldError id="signup-properties-error" message={errors.propertyCount} />
                </div>

                <fieldset>
                  <legend className="text-sm font-medium text-gray-900">I am a…</legend>
                  <RadioGroup
                    value={role}
                    onValueChange={(v) => setRole(v as SignupRole)}
                    className="mt-2 space-y-1"
                    aria-invalid={Boolean(errors.role)}
                    data-testid="radio-signup-role"
                  >
                    {ROLE_OPTIONS.map((opt) => (
                      <label
                        key={opt.value}
                        htmlFor={`signup-role-${opt.value}`}
                        className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 hover:bg-gray-50"
                      >
                        <RadioGroupItem
                          id={`signup-role-${opt.value}`}
                          value={opt.value}
                          data-testid={`radio-role-${opt.value}`}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </RadioGroup>
                  <FieldError id="signup-role-error" message={errors.role} />
                </fieldset>

                {!showPhone ? (
                  <button
                    type="button"
                    onClick={() => setShowPhone(true)}
                    className="text-sm font-medium text-brand hover:underline"
                    data-testid="button-add-phone"
                  >
                    Add a phone number for text alerts (optional)
                  </button>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="signup-phone">Phone number for text alerts (optional)</Label>
                      <Input
                        id="signup-phone"
                        type="tel"
                        autoComplete="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        aria-invalid={Boolean(errors.phone)}
                        className="mt-1.5"
                        data-testid="input-signup-phone"
                      />
                      <FieldError id="signup-phone-error" message={errors.phone} />
                    </div>
                    {phone.trim() !== "" && (
                      <label className="flex cursor-pointer items-start gap-2 text-xs text-gray-600 select-none">
                        <Checkbox
                          checked={smsConsent}
                          onCheckedChange={(v) => setSmsConsent(v === true)}
                          className="mt-0.5"
                          data-testid="checkbox-signup-sms-consent"
                        />
                        <span>
                          I agree to receive transactional and marketing SMS messages from PadPulse at the number above.
                          Message and data rates may apply. Reply STOP to opt out. See our{" "}
                          <a href="/terms#sms" target="_blank" rel="noopener noreferrer" className="text-brand underline">
                            SMS Terms
                          </a>{" "}
                          and{" "}
                          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand underline">
                            Privacy Policy
                          </a>
                          .
                        </span>
                      </label>
                    )}
                    <FieldError id="signup-sms-consent-error" message={errors.smsConsent} />
                  </div>
                )}

                <div>
                  <label className="flex cursor-pointer items-start gap-2 text-sm text-gray-700 select-none">
                    <Checkbox
                      checked={acceptedTerms}
                      onCheckedChange={(v) => setAcceptedTerms(v === true)}
                      aria-invalid={Boolean(errors.acceptedTerms)}
                      className="mt-0.5"
                      data-testid="checkbox-signup-terms"
                    />
                    <span>
                      I agree to the{" "}
                      <a href={TERMS_PRICING_HREF} target="_blank" rel="noopener noreferrer" className="text-brand underline" data-testid="link-signup-terms">
                        Terms of Service
                      </a>{" "}
                      and{" "}
                      <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand underline">
                        Privacy Policy
                      </a>
                      .
                    </span>
                  </label>
                  <FieldError id="signup-terms-error" message={errors.acceptedTerms} />
                </div>

                {errors.form && (
                  <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700" role="alert" data-testid="error-signup-form">
                    {errors.form}
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={pending}
                  className="h-auto min-h-12 w-full whitespace-normal py-3 leading-snug bg-brand-accent text-base font-semibold text-white hover:bg-brand-accent-hover"
                  data-testid="button-signup-submit"
                >
                  {pending ? <Loader2 className="h-5 w-5 animate-spin" aria-label="Submitting" /> : submitLabel}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleVerify} noValidate className="space-y-5" data-testid="form-signup-code">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Check your email</h2>
                  <p className="mt-1 text-sm text-gray-600" data-testid="text-code-sent">
                    We sent a 6-digit code to <span className="font-medium text-gray-900">{submittedEmail}</span>. Enter it
                    below to finish creating your account.
                  </p>
                </div>
                <div>
                  <Label htmlFor="signup-code">6-digit code</Label>
                  <Input
                    id="signup-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    aria-invalid={Boolean(errors.code)}
                    className="mt-1.5 text-lg tracking-[0.3em]"
                    data-testid="input-signup-code"
                  />
                  <FieldError id="signup-code-error" message={errors.code} />
                </div>
                {errors.form && (
                  <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700" role="alert" data-testid="error-signup-form">
                    {errors.form}
                  </p>
                )}
                {resent && !errors.form && (
                  <p className="text-sm text-emerald-700" data-testid="text-code-resent">
                    We sent a new code.
                  </p>
                )}
                <Button
                  type="submit"
                  disabled={pending}
                  className="h-auto min-h-12 w-full whitespace-normal py-3 leading-snug bg-brand-accent text-base font-semibold text-white hover:bg-brand-accent-hover"
                  data-testid="button-signup-verify"
                >
                  {pending ? <Loader2 className="h-5 w-5 animate-spin" aria-label="Verifying" /> : "Verify and continue"}
                </Button>
                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={pending}
                    className="font-medium text-brand hover:underline disabled:opacity-50"
                    data-testid="button-resend-code"
                  >
                    Send a new code
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStep("form");
                      setCode("");
                      setErrors({});
                      setResent(false);
                    }}
                    className="text-gray-600 hover:underline"
                    data-testid="button-change-email"
                  >
                    Use a different email
                  </button>
                </div>
              </form>
            )}
          </div>

          <p className="mt-6 text-center text-sm text-gray-600">
            Already have an account?{" "}
            <Link href={LOGIN_PATH} onClick={() => { if (wantsLifetime) stashReturnTo(lifetimeClaimPath(lifetimeUnits, lifetimeEventId)); }} className="font-medium text-brand hover:underline" data-testid="link-signup-login">
              Log in
            </Link>
          </p>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
